import React from 'react';
import PropTypes from 'prop-types';
import { List, ListItem, Page, Searchbar } from 'framework7-react';

import { I18n } from 'Locales';
import { User } from 'Repositories';
import { RowView } from 'Containers';
import { AppHeader } from 'Components';
import { NavigationService } from 'Services';
import { PAGE_NAMES, REGIONS } from 'Constants';
import { debounce, mockFunction } from 'Helpers';

import './style.scss';

const handleSearch = debounce((getPostCodes, updateTransientProps, searchSuccess, filteredAddressByPostCodes, e) => {
  const { value } = e.target;
  const trimmedValue = value.trim();

  if (trimmedValue.length < 4) {
    return;
  }

  if (
    searchSuccess !== value.substring(0, searchSuccess.length) ||
    searchSuccess.length === 0 ||
    filteredAddressByPostCodes?.length === 0
  ) {
    getPostCodes(value);
    updateTransientProps({ searchSuccess: value });
  }
  updateTransientProps({ search: value });
}, 500);

const handleLoqateSearch = debounce((getLoqateAddress, country, updateTransientProps, e) => {
  const text = e.target.value.trim();
  if (text.length === 0) {
    updateTransientProps({
      loqate_address: []
    });
  } else {
    const data = { text, country };
    getLoqateAddress(data);
  }
}, 500);

const filteredAddressByFullName = (filteredAddressByPostCodes, search = '', searchSuccess = '') =>
  filteredAddressByPostCodes?.filter(element =>
    element.full_address?.includes(search.substring(searchSuccess.length, search.length))
  );

const onClickHandler = (
  updateTransientProps,
  updateUser,
  address_line1,
  address_line2,
  address_line3,
  city,
  postcode
) => {
  const city_has_error = city.length === 0;
  const postcode_has_error = postcode.length === 0;
  const address_line1_has_error = address_line1.length === 0;

  updateTransientProps({
    address_line1_has_error,
    address_line1,
    address_line2,
    address_line3,
    city_has_error,
    city,
    filteredAddressByPostCodes: [],
    postcode_has_error,
    postcode
  });

  const newUser = {
    data: {
      attributes: {
        address_line1,
        city,
        postcode,
        address_line2,
        address_line3
      }
    }
  };
  updateUser(newUser);

  NavigationService.goBack({ name: PAGE_NAMES.PROFILE });
};

const onClickLoqateHandler = ({ country, updateTransientProps, updateUser, item }) => {
  const data = { ...item, country };

  User.getLoqateAddress(data).then(cb => {
    if (cb.status === 200) {
      const loqate_address_find = cb.data[0];
      const city_has_error = loqate_address_find.town === 0;
      const postcode_has_error = loqate_address_find.postcode === 0;
      const address_line1_has_error = loqate_address_find.address_line1 === 0;

      const newUser = {
        data: {
          attributes: {
            city: loqate_address_find.town,
            postcode: loqate_address_find.postcode,
            address_line1: loqate_address_find.address_line1,
            address_line2: loqate_address_find.address_line2,
            address_line3: loqate_address_find.address_line3
          }
        }
      };
      updateUser(newUser);

      updateTransientProps({
        address_line1_has_error,
        city_has_error,
        postcode_has_error,
        loqate_address: []
      });

      NavigationService.goBack({ name: PAGE_NAMES.PROFILE });
    }
  });
};

const PostCodesList = ({
  country,
  filteredAddressByPostCodes,
  getLoqateAddress,
  getPostCodes,
  loqate_address,
  search,
  searchSuccess,
  updateTransientProps,
  updateUser
}) => (
  <Page id="profile-postcodes">
    <AppHeader
      user
      goBack
      title={I18n.t('profile:contactTab.addressFinder')}
      handleClick={NavigationService.goBack.bind(null, { name: PAGE_NAMES.PROFILE })}
    />
    <RowView>
      <Searchbar
        onChange={
          REGIONS.UK.includes(country)
            ? handleLoqateSearch.bind(null, getLoqateAddress, country, updateTransientProps)
            : handleSearch.bind(null, getPostCodes, updateTransientProps, searchSuccess, filteredAddressByPostCodes)
        }
        onClickClear={
          REGIONS.UK.includes(country)
            ? updateTransientProps.bind(null, { loqate_address: [] })
            : updateTransientProps.bind(null, { search: '' })
        }
        placeholder={I18n.t(`profile:contactTab.${REGIONS.UK.includes(country) ? 'searchUK' : 'search'}`)}
      />
    </RowView>
    {loqate_address && !!loqate_address.length ? (
      <List className="mt-0">
        {loqate_address.map((item, index) => {
          const { id, type, full_address } = item;
          const address_params = {
            country,
            id,
            type,
            text: full_address.split('(More Addresses)')[0].trim()
          };

          return (
            <ListItem
              link
              key={index}
              onClick={
                item.type === 'Address'
                  ? onClickLoqateHandler.bind(null, { country, item, updateTransientProps, updateUser })
                  : getLoqateAddress.bind(null, address_params)
              }>
              {item.full_address}
            </ListItem>
          );
        })}
      </List>
    ) : (
      <List className="mt-0">
        {filteredAddressByFullName(filteredAddressByPostCodes, search, searchSuccess)?.map((addressDetails, index) => (
          <ListItem
            link
            key={index}
            onClick={onClickHandler.bind(
              null,
              updateTransientProps,
              updateUser,
              addressDetails.address_line1,
              addressDetails.address_line2,
              addressDetails.address_line3,
              addressDetails.town,
              addressDetails.postcode,
              filteredAddressByPostCodes
            )}>
            {addressDetails.full_address}
          </ListItem>
        ))}
      </List>
    )}
  </Page>
);

PostCodesList.propTypes = {
  country: PropTypes.string,
  filteredAddressByPostCodes: PropTypes.array,
  getLoqateAddress: PropTypes.func,
  getPostCodes: PropTypes.func,
  loqate_address: PropTypes.array,
  search: PropTypes.string,
  searchSuccess: PropTypes.string,
  updateTransientProps: PropTypes.func,
  updateUser: PropTypes.func
};

PostCodesList.defaultProps = {
  country: '',
  filteredAddressByPostCodes: [],
  getLoqateAddress: mockFunction,
  getPostCodes: mockFunction,
  loqate_address: [],
  search: '',
  searchSuccess: '',
  updateTransientProps: mockFunction,
  updateUser: mockFunction
};

export default PostCodesList;
